import React, { useEffect, useMemo, useState } from "react";
import { Gauge, RefreshCw, Info, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { fmtDateGB } from "../lib/format";

/**
 * GitHub-style meter-sync coverage calendar heatmap.
 *
 * Rows = day-of-week (Mon → Sun), Columns = weeks. Each cell is coloured by
 * how many 15-min samples landed for that day (96 = a full 24h of HH data):
 *   red    → 0 quarters  (sync failed / no data)
 *   amber  → partial     (sync gap — missing part of the day)
 *   green  → ~complete   (healthy MeterOnline / HH sync)
 *
 * **Click** a red / amber cell to re-pull HH data for a ±3-day window around
 * that date via the MeterOnline backfill-range endpoint — one-click remediation
 * of sync gaps, not just a diagnostic.
 * Powered by `GET /api/monitoring/{pid}/meter-coverage?lookback_days=N`.
 */
export default function MeterSyncCoverageHeatmap({ projectId, defaultLookback = 365, onBackfilled }) {
  const [lookback, setLookback] = useState(defaultLookback);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyDate, setBusyDate] = useState(null);
  const [tip, setTip] = useState(null); // { x, y, date, quarters }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/monitoring/${projectId}/meter-coverage?lookback_days=${lookback}`,
      );
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load meter coverage");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [projectId, lookback]);

  // Bucket days into weeks (columns). Week columns are Mon-anchored.
  const weeks = useMemo(() => {
    if (!data?.days) return [];
    const first = new Date(data.days[0].date + "T00:00:00Z");
    const firstDow = (first.getUTCDay() + 6) % 7; // 0=Mon..6=Sun
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (const d of data.days) cells.push(d);
    const cols = [];
    for (let i = 0; i < cells.length; i += 7) cols.push(cells.slice(i, i + 7));
    return cols;
  }, [data]);

  // Month labels — first week where the month changes gets a label.
  const monthLabels = useMemo(() => {
    if (!weeks.length) return [];
    const labels = [];
    let lastMonth = -1;
    weeks.forEach((col, i) => {
      const first = col.find(Boolean);
      if (!first) return labels.push({ i, text: "" });
      const m = new Date(first.date + "T00:00:00Z").getUTCMonth();
      if (m !== lastMonth) {
        labels.push({ i, text: new Date(first.date + "T00:00:00Z")
          .toLocaleString(undefined, { month: "short", timeZone: "UTC" }) });
        lastMonth = m;
      } else {
        labels.push({ i, text: "" });
      }
    });
    return labels;
  }, [weeks]);

  const todayIso = new Date().toISOString().slice(0, 10);

  const cellClass = (d) => {
    if (!d) return "bg-transparent";
    const q = d.quarters;
    // Future / today — not yet graded (sync runs daily at 03:00 UTC).
    if (d.date >= todayIso) return "bg-zinc-200";
    if (q >= 92) return "bg-emerald-500";
    if (q >= 48) return "bg-emerald-300";
    if (q >= 12) return "bg-amber-300";
    if (q >= 1)  return "bg-amber-500";
    return "bg-red-400";
  };

  const handleCellClick = async (d) => {
    if (!d || d.date >= todayIso) return;
    if (d.quarters >= 92) {
      toast.info(`${fmtDateGB(d.date)} already has ${d.quarters} / 96 samples`);
      return;
    }
    const base = new Date(d.date + "T00:00:00Z");
    const s = new Date(base.getTime() - 3 * 86400000).toISOString().slice(0, 10);
    let e = new Date(base.getTime() + 3 * 86400000).toISOString().slice(0, 10);
    if (e > todayIso) e = todayIso;
    if (!window.confirm(`Re-pull HH meter data for ${s} → ${e} to fill the gap around ${fmtDateGB(d.date)}?`)) return;
    setBusyDate(d.date);
    try {
      const { data: r } = await api.post(`/monitoring/${projectId}/backfill-range`, {
        start_date: s, end_date: e, fill_gaps_only: true,
      });
      if (r.errors && r.errors.length) {
        toast.error(`Backfill wrote ${r.written} samples · ${r.errors.length} error(s): ${r.errors[0]}`);
      } else {
        toast.success(`Backfilled ${r.written} samples across ${r.days_fetched} day(s)`);
      }
      await load();
      onBackfilled && onBackfilled();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Backfill failed");
    } finally { setBusyDate(null); }
  };

  return (
    <section className="swiss-card-flat p-6 relative" data-testid="meter-sync-coverage-heatmap">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="label flex items-center gap-2"><Gauge size={11} /> Meter-sync calendar</div>
          <div className="font-display text-lg font-semibold mt-1">
            HH / 15-min samples per day
          </div>
          {data && (
            <div className="text-xs muted mt-1 font-mono">
              {data.total_quarters_have.toLocaleString()} / {data.total_quarters_expected.toLocaleString()} samples
              &nbsp;·&nbsp; <span className="font-bold">{data.coverage_pct}%</span> covered
              {(data.missing_days > 0 || data.gap_days > 0) && (
                <span className="ml-2 inline-flex items-center gap-1 text-[var(--warm-hover)]">
                  <AlertTriangle size={10} />
                  {data.missing_days} missing · {data.gap_days} partial day(s)
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select value={lookback}
                  onChange={(e) => setLookback(Number(e.target.value))}
                  className="input-swiss text-xs py-1"
                  data-testid="meter-coverage-lookback">
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last year</option>
            <option value={730}>Last 2 years</option>
            <option value={1095}>Last 3 years</option>
          </select>
          <button className="btn-ghost text-[10px]" onClick={load} disabled={loading}
                  data-testid="meter-coverage-refresh">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {loading && !data && <div className="text-xs muted">Loading…</div>}

      {data && weeks.length > 0 && (
        <div className="overflow-x-auto pb-2">
          {/* Month labels row */}
          <div className="flex gap-[3px] ml-8 mb-1">
            {monthLabels.map((m) => (
              <div key={m.i}
                   className="text-[9px] muted font-mono uppercase tracking-widest"
                   style={{ width: 12, minWidth: 12 }}>
                {m.text}
              </div>
            ))}
          </div>

          <div className="flex">
            {/* Day-of-week labels */}
            <div className="flex flex-col gap-[3px] mr-2 pt-[2px] text-[9px] muted font-mono uppercase">
              {["Mon", "", "Wed", "", "Fri", "", "Sun"].map((d, i) => (
                <div key={i} style={{ height: 12, lineHeight: "12px" }}>{d}</div>
              ))}
            </div>
            {/* Grid */}
            <div className="flex gap-[3px]" data-testid="meter-coverage-grid">
              {weeks.map((col, ci) => (
                <div key={ci} className="flex flex-col gap-[3px]">
                  {col.map((d, ri) => (
                    <div
                      key={ri}
                      className={`w-3 h-3 ${cellClass(d)} rounded-[2px] ${d && d.date < todayIso ? "cursor-pointer" : "cursor-default"} transition-transform hover:scale-125 ${busyDate === d?.date ? "ring-2 ring-[var(--accent)] animate-pulse" : ""}`}
                      onClick={() => handleCellClick(d)}
                      onMouseEnter={(e) => {
                        if (!d) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTip({
                          x: rect.left + rect.width / 2,
                          y: rect.top - 6,
                          date: d.date,
                          quarters: d.quarters,
                          future: d.date >= todayIso,
                        });
                      }}
                      onMouseLeave={() => setTip(null)}
                      data-testid={d ? `meter-cell-${fmtDateGB(d.date)}` : undefined}
                      title={d ? (d.date >= todayIso ? `${fmtDateGB(d.date)} · pending sync` : (d.quarters >= 92 ? `${fmtDateGB(d.date)} · ${d.quarters}/96 (complete)` : `${fmtDateGB(d.date)} · ${d.quarters}/96 — click to backfill`)) : undefined}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 mt-4 text-[10px] muted font-mono uppercase tracking-widest flex-wrap">
            <span>Gap</span>
            <div className="w-3 h-3 bg-red-400 rounded-[2px]" title="0 samples — sync failed" />
            <div className="w-3 h-3 bg-amber-500 rounded-[2px]" title="1-11 samples" />
            <div className="w-3 h-3 bg-amber-300 rounded-[2px]" title="12-47 samples" />
            <div className="w-3 h-3 bg-emerald-300 rounded-[2px]" title="48-91 samples" />
            <div className="w-3 h-3 bg-emerald-500 rounded-[2px]" title="92-96 samples" />
            <span>Full</span>
            <div className="w-3 h-3 bg-zinc-200 rounded-[2px]" title="Today / pending sync" />
            <span>Pending</span>
            <span className="ml-auto flex items-center gap-1 normal-case tracking-normal">
              <Info size={10} /> Click a red / amber cell to re-pull meter data around that date.
            </span>
          </div>
        </div>
      )}

      {data && weeks.length === 0 && !loading && (
        <div className="text-xs muted py-6 text-center">No meter samples in this window yet.</div>
      )}

      {/* Absolute tooltip (fixed to viewport) */}
      {tip && (
        <div className="fixed z-50 pointer-events-none px-2 py-1 bg-black/85 text-white text-[10px] font-mono rounded"
             style={{ left: tip.x, top: tip.y, transform: "translate(-50%, -100%)" }}
             data-testid="meter-coverage-tooltip">
          {tip.date} · {tip.future ? <span className="opacity-80">pending sync</span> : <><strong>{tip.quarters}</strong> / 96 samples{tip.quarters < 92 && <span className="ml-1 opacity-80">· click to backfill</span>}</>}
        </div>
      )}
    </section>
  );
}
